import { FC } from "react";

import { RegisterOptions, UseFormReturn } from "react-hook-form";

import { NewsletterFormSignupData } from "../types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardCheckboxGroup } from "@/dashboard-design-system/DashboardCheckboxGroup";

// The group spaces its rows 4px apart; the design puts 8px between them
const CHOICE_ROW_STYLE = { marginTop: 8 } as const;

type NewsletterQuestionChoicesFieldProps = {
  form: UseFormReturn<NewsletterFormSignupData>;
  index: number;
  label: string;
  required: boolean;
  choices: Array<{ id: string; body: string }>;
};

export const NewsletterQuestionChoicesField: FC<NewsletterQuestionChoicesFieldProps> = ({
  form,
  index,
  label,
  required,
  choices,
}) => {
  const { t } = useTranslate();

  // react-hook-form types the error of a list field as a branch, not a leaf, so the message needs narrowing
  const errorMessage = (form.formState.errors.questions?.[index]?.choiceIds as { message?: string } | undefined)
    ?.message;

  const rules: RegisterOptions[] = required
    ? [
        {
          validate: {
            hasCheckedChoice: (value: unknown) =>
              (Array.isArray(value) && value.length > 0) || t("common", "answerRequired"),
          },
        },
      ]
    : [];

  // The group never writes to the form itself, it only reports the ticked map back to its caller
  const handleChange = (values: Record<string, boolean>) => {
    const checkedChoiceIds = Object.entries(values)
      .filter(([, isChecked]) => isChecked)
      .map(([choiceId]) => choiceId);

    form.setValue(`questions.${index}.choiceIds`, checkedChoiceIds, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <DashboardCheckboxGroup
      control={form.control}
      name={`questions.${index}.choiceIds`}
      label={label}
      rules={rules}
      onChange={handleChange}
      // Small would shrink the box to 16px and the label below the design's body1
      size="medium"
      checkboxes={choices.map((choice, choiceIndex) => ({
        choice: choice.id,
        label: choice.body,
        ...(choiceIndex > 0 && { style: CHOICE_ROW_STYLE }),
      }))}
      error={Boolean(errorMessage)}
      errorMessage={errorMessage}
      data-test="form-question-multiple-checkboxes"
    />
  );
};
